// Création du composant Sortable Item
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { Button } from "@repo/ui";

interface SortableItemProps {
  id: string;
  label: string;
  index: number; // Position de l'item (0, 1, 2)
  editable?: boolean;
}

// Fonction pour obtenir la classe du badge selon la position
const getPositionBadgeClass = () => {
  return "bg-muted text-foreground border-border";
};

const getPositionLabel = (index: number) => {
  const labels = ["1er", "2e", "3e"];
  return labels[index] || index + 1;
};

export function SortableItem({ id, label, index, editable = true }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || "all 200ms ease",
    zIndex: isDragging ? 1000 : 1,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 bg-card border rounded-md shadow-sm transition-all duration-200 ${
        isDragging
          ? "border-2 border-primary shadow-lg scale-105 ring-2 ring-primary ring-opacity-30"
          : isOver
            ? "border-2 border-primary bg-primary/5 shadow-md"
            : "border border-border hover:border-accent hover:shadow-md"
      }`}
    >
      {/* Badge numéroté de position */}
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-full font-bold text-xs flex items-center justify-center border ${getPositionBadgeClass()}`}
      >
        {getPositionLabel(index)}
      </div>

      {/* Le "handle" pour glisser l'élément */}
      {editable && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="flex-shrink-0 cursor-grab active:cursor-grabbing h-6 w-6 hover:bg-accent/50 transition-colors p-0"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="text-muted-foreground" size={16} />
        </Button>
      )}

      {/* Texte de l'affirmation */}
      <span className="text-sm select-none flex-1 leading-tight">
        {label}
      </span>
    </div>
  );
}